import { useState } from 'react';
import { Box } from '@mui/material';
import { UserSummaryTable } from '../../components/UserSummaryTable';
import { UserTable } from '../../components/UserTable';
import { UserDetailDialog } from '../../components/UserDetailDialog';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import type { User } from '../../type';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { ExportExcelButton } from '../../components/ExportExcelButton';
import { downloadEncryptedExcel } from '../../service/encryptedExportService';

interface UserContentProps {
  users: User[];
  loading: boolean;
  error: string | null;
}

const UserContent = ({
  users,
  loading,
  error,
}: UserContentProps) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>유저 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  const handleExport = async (): Promise<void> => {
    if (users.length === 0) {
      alert('내보낼 사용자 데이터가 없습니다.');
      return;
    }

    const exportData = users.map(user => ({
      '사용자 ID': user.uid,
      '이메일': user.email,
      '이름': user.displayName,
      '전화번호': user.phoneNumber,
      '사용자 타입': user.isEnterpriseUser ? '기업 사용자' : '개인 사용자',
      '기업 ID': user.enterpriseId ?? '',
      '가입일': user.createdAt.toDate().toISOString(),
      '상태': user.status,
      '선택 약관 동의': user.termsAgreed.optional ? '동의' : '미동의',
    }));

    setIsExporting(true);
    try {
      await downloadEncryptedExcel(exportData, '사용자 목록', '사용자목록');
    } catch (error) {
      console.error('Encrypted user export failed:', error);
      alert('암호화 ZIP 생성에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AnimatedBox>
      <Box sx={{ mb: 3 }}>
        <PageTitle>유저 관리</PageTitle>
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      {users.length === 0 ? (
        <EmptyState message="아직 등록된 유저가 없습니다." />
      ) : (
        <Box sx={{
          display: 'flex',
          gap: 2,
        }}>
          {/* <UserSummaryTable totalUsers={users.length} /> */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ExportExcelButton onClick={handleExport} loading={isExporting} />
            <UserSummaryTable totalUsers={users.length} />
          </Box>
          <UserTable
            data={users}
            onUserSelect={setSelectedUser}
          />
        </Box>
      )}
      <UserDetailDialog user={selectedUser} onClose={() => setSelectedUser(null)} />
    </AnimatedBox>
  );
};

export default UserContent;
